import type { ViewType } from "../types";

export interface ShortcutEntry {
  keys: string[];
  /**
   * Plain-text description with optional `[X]`-style markers around the
   * letter(s) that map to the shortcut key, e.g. `"[N]ew task"` for `N` or
   * `"[C]onvert to a [t]ask"` for the C → T chord. The rendering layer
   * (`ShortcutDescription`) emphasises the bracketed letters. Letters
   * outside brackets render as plain text. Entries whose key has no
   * letter mapping (e.g. `?`, `Esc`, arrow keys) leave the description
   * unmarked.
   */
  description: string;
  /**
   * When true, keys are pressed in sequence (a chord) rather than together,
   * so they're separated by "then" instead of "+".
   */
  chord?: boolean;
  /**
   * When true, the entries in `keys` are independent alternatives that each
   * trigger the action (e.g. the four arrow keys that move the board
   * highlight), so they render side by side with no joiner between them
   * instead of a "+"/"then" separator.
   */
  keysAreAlternatives?: boolean;
  /**
   * Renders a small sub-heading line directly before this entry's row. Use
   * to label a group of adjacent entries inside a section (e.g. the
   * view-switcher shortcuts inside the Global section).
   */
  subheadingBefore?: string;
  /**
   * Visual nesting depth of the row's description, 1 or 2. Level 1 sits under
   * a top-level sub-heading; level 2 sits under a nested sub-heading (e.g. the
   * calendar's "Filter (cycle)" group inside "Unscheduled & overdue sidebar").
   */
  indentLevel?: number;
  /**
   * Visual nesting depth of `subheadingBefore`, when the sub-heading itself is
   * nested under another (e.g. "Filter (cycle)" indented one level).
   */
  subheadingIndentLevel?: number;
}

export interface ShortcutSection {
  id: string;
  title: string;
  /**
   * Optional contextual note appended to the title in Settings (e.g. "when
   * enabled" for the Email section, which depends on Gmail being connected).
   * The contextual `?` dialog ignores it because that dialog only surfaces
   * a section when the user is actually on the matching view, so the hint
   * is redundant there.
   */
  availabilityHint?: string;
  /**
   * If set, this section only applies inside the named view. The `?` dialog
   * uses this to filter and highlight the relevant section for the current
   * view. Sections without `view` are always shown.
   */
  view?: ViewType;
  /**
   * Optional explanatory note rendered as a small line beneath the section's
   * shortcut rows. Use for context a key cap can't convey on its own (e.g.
   * clarifying that "Next / Previous task" steps through the board's currently
   * visible tasks, which active filters can change).
   */
  note?: string;
  shortcuts: ShortcutEntry[];
}

export const shortcutSections: ShortcutSection[] = [
  {
    id: "global",
    title: "Global",
    shortcuts: [
      {
        keys: ["B"],
        description: "[B]oard view",
        subheadingBefore: "Views",
        indentLevel: 1,
      },
      { keys: ["C"], description: "[C]alendar view", indentLevel: 1 },
      { keys: ["E"], description: "[E]mails view", indentLevel: 1 },
      { keys: ["N"], description: "[N]ew task" },
      { keys: ["V"], description: "[V]oice recording" },
      { keys: ["/"], description: "Search" },
      { keys: ["Esc"], description: "Clear search" },
      { keys: ["⌥", "S"], description: "[S]ettings" },
      { keys: ["?"], description: "Show keyboard shortcuts" },
    ],
  },
  {
    id: "board",
    title: "Board view",
    view: "board",
    shortcuts: [
      {
        keys: ["↑", "↓", "←", "→"],
        keysAreAlternatives: true,
        description: "Move highlight",
        subheadingBefore: "Navigate tasks",
        indentLevel: 1,
      },
      {
        keys: ["Enter"],
        description: "Open the highlighted task",
        indentLevel: 1,
      },
      {
        keys: ["S"],
        description: "[S]cheduled",
        subheadingBefore: "Built-in filters (cycle)",
        indentLevel: 1,
      },
      { keys: ["P"], description: "[P]riority", indentLevel: 1 },
      { keys: ["T"], description: "[T]ag", indentLevel: 1 },
      { keys: ["L"], description: "[L]ist", indentLevel: 1 },
      { keys: ["9"], description: "[9]–5 (Working hours)", indentLevel: 1 },
      {
        keys: ["1"],
        description: "Previous",
        subheadingBefore: "Custom filters (cycle)",
        indentLevel: 1,
      },
      { keys: ["2"], description: "Next", indentLevel: 1 },
      { keys: ["Esc"], description: "Clear filters" },
      { keys: ["+"], description: "Expand/collapse all subtasks" },
      { keys: ["H"], description: "Toggle completed tasks" },
      { keys: ["R"], description: "[R]eorder lists" },
    ],
  },
  {
    id: "calendar",
    title: "Calendar view",
    view: "calendar",
    shortcuts: [
      { keys: ["W"], description: "[W]eek view" },
      { keys: ["D"], description: "[D]ay view" },
      { keys: ["T"], description: "Jump to [t]oday" },
      { keys: ["←"], description: "Previous week / day" },
      { keys: ["→"], description: "Next week / day" },
      {
        keys: ["S"],
        description: "[S]how/hide sidebar",
        subheadingBefore: "Unscheduled & overdue sidebar",
        indentLevel: 1,
      },
      {
        keys: ["P"],
        description: "[P]riority",
        subheadingBefore: "Filter (cycle)",
        subheadingIndentLevel: 1,
        indentLevel: 2,
      },
      { keys: ["T"], description: "[T]ag", indentLevel: 2 },
      { keys: ["L"], description: "[L]ist", indentLevel: 2 },
      { keys: ["9"], description: "[9]–5 (Working hours)", indentLevel: 2 },
    ],
  },
  {
    id: "emails",
    title: "Email view",
    availabilityHint: "when enabled",
    view: "emails",
    shortcuts: [
      { keys: ["→"], description: "Next conversation" },
      { keys: ["←"], description: "Previous conversation" },
      { keys: ["A"], description: "[A]rchive conversation" },
      { keys: ["T"], description: "[T]ranslate / show original" },
      { keys: ["G"], description: "Open in [G]mail" },
      { keys: ["S", "T"], description: "[S]ave as a [t]ask", chord: true },
      { keys: ["S", "S"], description: "[S]ave as a [s]ubtask", chord: true },
      {
        keys: ["S", "L"],
        description: "[S]ave as a [l]ink on a task/subtask",
        chord: true,
      },
      {
        keys: ["S", "E"],
        description: "[S]ave as a calendar [e]vent",
        chord: true,
      },
    ],
  },
  {
    id: "task-dialog",
    title: "Task dialog",
    note: "Next and previous step through the tasks currently visible on your board, so any filters you've applied change which tasks they move between. On a Today filter, they step through the tasks and sub-tasks that are for today, and skip a task that is only there so you can see those sub-tasks.",
    shortcuts: [
      { keys: ["⌥", "D"], description: "Next task" },
      { keys: ["⌥", "A"], description: "Previous task" },
    ],
  },
];
